import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { DatePicker } from "components";
import { useDateFieldQuestionStyles } from "./style";
import { useTranslation } from "react-i18next";
import { hasValidDateFormat, hasInvalidDateInput } from "../../shared/validation";

interface DateFieldQuestionProps {
  questionId: string;
  questionText: string;
  isMandatory: boolean;
  questionHint?: string;
  value: string;
  hasError?: boolean;
  showInvalidInputError?: boolean;
  customInvalidInputMessage?: string;
  onChange: (questionId: string, value: string, invalidDate?: boolean) => void;
}

export const DateFieldQuestion: React.FC<DateFieldQuestionProps> = ({
  questionId,
  questionText,
  isMandatory,
  questionHint,
  value,
  hasError = false,
  showInvalidInputError = false,
  customInvalidInputMessage,
  onChange,
}) => {
  const classes = useDateFieldQuestionStyles();

  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);

  const inputId = `date-${questionId}`;
  const labelId = `date-label-${questionId}`;
  const { t } = useTranslation();

  const parseStoredDate = (dateStr: string): Date => {
    const d = new Date(dateStr);
    d.setUTCHours(12, 0, 0, 0);
    return d;
  };

  const [selectedValue, setSelectedValue] = useState<Date | null>(
    value ? parseStoredDate(value) : null,
  );
  const [invalidDate, setInvalidDate] = useState(false);
  const [pastDateError, setPastDateError] = useState(false);

  useEffect(() => {
    setSelectedValue(value ? parseStoredDate(value) : null);
  }, [value]);

  const toUTCDateString = (date: Date) => {
    return new Date(Date.UTC(date?.getFullYear(), date?.getMonth(), date?.getDate(), 12, 0, 0))
      .toISOString()
      .split("T")[0];
  };

  const updateDateValue = (
    date: Date | null,
    hasInvalidDate: boolean,
    hasPastDateError: boolean,
    nextValue: string,
  ) => {
    setSelectedValue(date);
    setInvalidDate(hasInvalidDate);
    setPastDateError(hasPastDateError);
    onChange(questionId, nextValue, hasInvalidDate || hasPastDateError);
  };

  const handleOnBlur = (date: Date | null, inputValue: string) => {
    const resolvedDate = date instanceof Date && !isNaN(date.getTime()) ? date : selectedValue;

    const hasInvalidInput = resolvedDate ? false : hasInvalidDateInput(inputValue, date);
    const hasPastDate = Boolean(
      resolvedDate && hasValidDateFormat(inputValue) && resolvedDate < minDate,
    );

    setInvalidDate(hasInvalidInput);
    setPastDateError(hasPastDate);

    onChange(
      questionId,
      resolvedDate ? toUTCDateString(resolvedDate) : "",
      hasInvalidInput || hasPastDate,
    );
  };

  const handleOnChange = (value: Date | null) => {
    if (value?.getFullYear().toString().length === 4) {
      const isInvalid = !value || isNaN(value.getTime());
      const isPast = !isInvalid && value < minDate;
      updateDateValue(value, isInvalid, isPast, isInvalid ? "" : toUTCDateString(value));
    } else {
      setSelectedValue(value);
    }
  };

  return (
    <Box className={`${classes.container} commissioning-date-field`}>
      <DatePicker
        asteriskClassName={classes.requiredAsterisk}
        label={questionText}
        labelClassName={classes.label}
        id={inputId}
        labelId={labelId}
        title={questionText}
        selectedValue={selectedValue}
        ariaLabelledBy={labelId}
        minDate={minDate}
        required={isMandatory}
        error={(hasError && isMandatory) || invalidDate || pastDateError || showInvalidInputError}
        errorMessage={
          showInvalidInputError && customInvalidInputMessage
            ? customInvalidInputMessage
            : invalidDate || pastDateError || showInvalidInputError
              ? `${questionText} ${t("invalid-date")}`
              : undefined
        }
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        rootClass={`${classes.datePickerRoot} ${hasError && isMandatory ? classes.hasError : ""}`}
      />
      {questionHint && <Typography className={classes.hint}>{questionHint}</Typography>}
    </Box>
  );
};
